import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  StyledModalBody,
  StyledModal,
  StyledModalFooter,
  QuestionContainer,
  QuestionText,
  QuestionInput,
  RadioGroup,
  RadioLabel,
  QuestionsWrapper,
} from "../styledComponents/StyledModals";
import { StyledButton } from "../styledComponents/CommonStyledComponents";
import { Input } from "reactstrap";
import { MUTATION_SET_MANUAL_EVENT_ANSWER_REQUEST } from "../graphql/mutations";
import { useApolloClient } from "@apollo/client";
import { toast } from "react-toastify";

export const RiskScoreManualDialog = ({
  showModal,
  rgDialogData,
  skin,
  setRgManualDialog,
}) => {
  const { t } = useTranslation("");
  const questions = rgDialogData.questions || [];
  const [answers, setAnswers] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const apolloClient = useApolloClient();

  const handleInputChange = (questionText, value, questionType) => {
    setAnswers((prev) => ({
      ...prev,
      [questionText]: value,
    }));

    // Clear validation error when user provides an answer
    if (validationErrors[questionText]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[questionText];
        return newErrors;
      });
    }
  };

  const validateAnswers = () => {
    const errors = {};
    questions.forEach((question) => {
      if (question.type === "NO_ANSWER") {
        return;
      }

      const answer = answers[question.text];
      const trimmedAnswer = answer?.trim() || "";
      // Remove all extra spaces (keeping only single spaces between words)
      const normalizedAnswer = trimmedAnswer.replace(/\s+/g, " ");

      if (!answer || trimmedAnswer === "") {
        errors[question.text] = t("answer_required");
      } else if (question.type === "INPUT" && normalizedAnswer.length < 14) {
        errors[question.text] = t("answer_minimum_14_characters");
      }
    });
    return errors;
  };

  const formatAnswersForMutation = () => {
    const formattedAnswers = questions.map((question) => ({
      question: question.text,
      answer: question.type === "NO_ANSWER" ? "" : answers[question.text] || "",
      type: question.type,
    }));
    return JSON.stringify(formattedAnswers);
  };

  const handleProceedWithAnswer = async () => {
    if (isSubmitting) return; // Prevent multiple submissions

    const errorsForQuestions = validateAnswers();

    if (Object.keys(errorsForQuestions).length > 0) {
      setValidationErrors(errorsForQuestions);
      return;
    }

    setIsSubmitting(true);

    const eventType = rgDialogData.event_type;
    const eventAnswers = formatAnswersForMutation();

    const { errors, data } = await apolloClient.mutate({
      mutation: MUTATION_SET_MANUAL_EVENT_ANSWER_REQUEST,
      variables: {
        eventType: eventType,
        eventAnswers: eventAnswers,
      },
      errorPolicy: "all",
    });

    if (errors) {
      toast.error(errors[0].message, {
        position: "bottom-center",
      });
      setIsSubmitting(false);
      return false;
    }

    if (data) {
      toast.success(t("label_success"), {
        position: "bottom-center",
      });
      setAnswers({});
      setValidationErrors({});
      setIsSubmitting(false);
      setRgManualDialog(false);
    }
  };

  const renderQuestion = (question, index) => {
    const { text, type } = question;
    const noAnswerLabels =
      question.labels || question.options || question.values || [];
    const labelsToShow =
      type === "NO_ANSWER"
        ? Array.isArray(noAnswerLabels)
          ? noAnswerLabels
          : []
        : [];
    const trimmedAnswer = answers[text]?.trim() || "";
    // Remove all extra spaces (keeping only single spaces between words)
    const normalizedLength = trimmedAnswer.replace(/\s+/g, " ").length;
    const isInputBelowMinimum =
      type === "INPUT" && normalizedLength > 0 && normalizedLength < 14;

    return (
      <QuestionContainer key={index}>
        <QuestionText>
          {text}
          {type !== "NO_ANSWER" && <span> *</span>}
        </QuestionText>

        {type === "INPUT" && (
          <>
            <QuestionInput
              type="text"
              value={answers[text] || ""}
              onChange={(e) => handleInputChange(text, e.target.value, type)}
              placeholder="..."
            />
            {isInputBelowMinimum && (
              <span
                style={{
                  fontSize: "12px",
                  color: "#ff9800",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {t("label_characters")}
              </span>
            )}
          </>
        )}

        {type === "RADIO" && (
          <RadioGroup>
            <RadioLabel>
              <Input
                type="radio"
                name={`question_${index}`}
                value="yes"
                checked={answers[text] === "yes"}
                onChange={(e) => handleInputChange(text, e.target.value, type)}
              />
              {t("label_yes")}
            </RadioLabel>
            <RadioLabel>
              <Input
                type="radio"
                name={`question_${index}`}
                value="no"
                checked={answers[text] === "no"}
                onChange={(e) => handleInputChange(text, e.target.value, type)}
              />
              {t("label_no")}
            </RadioLabel>
          </RadioGroup>
        )}

        {type === "NO_ANSWER" && (
          <RadioGroup>
            {labelsToShow.map((label, labelIndex) => (
              <RadioLabel key={`${text}_${labelIndex}`}>
                {String(label)}
              </RadioLabel>
            ))}
          </RadioGroup>
        )}
      </QuestionContainer>
    );
  };

  const allAnswersProvided = questions.every((question) => {
    if (question.type === "NO_ANSWER") {
      return true;
    }

    const answer = answers[question.text];
    const trimmedAnswer = answer?.trim() || "";
    // Remove all extra spaces (keeping only single spaces between words)
    const normalizedAnswer = trimmedAnswer.replace(/\s+/g, " ");

    if (trimmedAnswer === "") return false;
    if (question.type === "INPUT" && normalizedAnswer.length < 14) return false;
    return true;
  });

  const hasAnswerableQuestions = questions.some(
    (question) => question.type !== "NO_ANSWER",
  );

  return (
    <StyledModal isOpen={showModal}>
      <StyledModalBody riskScore>
        <p
          dangerouslySetInnerHTML={{
            __html: t("label_" + rgDialogData.event_type),
          }}
        />

        {questions.length > 0 && (
          <QuestionsWrapper>
            {questions.map((question, index) =>
              renderQuestion(question, index),
            )}
            {hasAnswerableQuestions && (
              <p>
                {questions.length === 1
                  ? t("label_field_required")
                  : t("label_all_fields_required")}
                <span>*</span>
              </p>
            )}
          </QuestionsWrapper>
        )}
      </StyledModalBody>

      <StyledModalFooter position>
        <StyledButton
          btnBold
          banktransfer={skin === "cac"}
          pub={skin === "pub"}
          onClick={handleProceedWithAnswer}
          disabled={!allAnswersProvided || isSubmitting}
          style={{
            opacity: allAnswersProvided && !isSubmitting ? 1 : 0.6,
            cursor:
              allAnswersProvided && !isSubmitting ? "pointer" : "not-allowed",
          }}
        >
          {!hasAnswerableQuestions
            ? t("label_ok")
            : questions.length === 1
              ? t("label_submit_answer")
              : t("label_submit_answers")}
        </StyledButton>
      </StyledModalFooter>
    </StyledModal>
  );
};
